/* eslint-disable @typescript-eslint/no-explicit-any */
// store/slices/executionHelpers.ts - Fixed execution helpers with improved streaming completion tracking
import { PayloadAction } from '@reduxjs/toolkit';
import { 
  AsyncExecution, 
  ExecutionProgressEvent, 
  StreamConnection,
  ExecutionStatus 
} from '@/types/AsyncExecution';

// =================== Response Extraction Utility ===================

/**
 * Extract response text from final output with comprehensive fallback logic
 */
const extractResponseFromFinalOutput = (finalOutput: any): string => {
  if (!finalOutput) {
    return 'Execution completed successfully, but no response content was returned.';
  }

  // Handle string response directly
  if (typeof finalOutput === 'string') {
    return finalOutput.trim() || 'Execution completed.';
  }

  // Try common response fields
  const responseFields = [
    'response', 'output', 'result', 'content', 'text', 'message', 
    'answer', 'reply', 'data', 'value'
  ];

  for (const field of responseFields) {
    if (finalOutput[field] && typeof finalOutput[field] === 'string') {
      const response = finalOutput[field].trim();
      if (response) return response;
    }
  }

  // Try nested response objects
  if (finalOutput.response && typeof finalOutput.response === 'object') {
    return extractResponseFromFinalOutput(finalOutput.response);
  }

  // Try to find any meaningful string content
  const searchForStrings = (obj: any, visited = new Set()): string | null => {
    if (visited.has(obj)) return null;
    visited.add(obj);

    if (typeof obj === 'string' && obj.trim().length > 0) {
      return obj.trim();
    }

    if (Array.isArray(obj)) {
      for (const item of obj) {
        const result = searchForStrings(item, visited);
        if (result) return result;
      }
    } else if (obj && typeof obj === 'object') {
      for (const value of Object.values(obj)) {
        const result = searchForStrings(value, visited);
        if (result) return result;
      }
    }

    return null;
  };

  const foundString = searchForStrings(finalOutput);
  if (foundString) return foundString;

  // Last resort: format as JSON if it contains meaningful data
  try {
    const outputStr = JSON.stringify(finalOutput, null, 2);
    if (outputStr !== '{}' && outputStr !== 'null' && outputStr !== '[]') {
      // If the JSON is reasonable length, return it formatted
      if (outputStr.length < 500) {
        return `Execution completed with result:\n\`\`\`json\n${outputStr}\n\`\`\``;
      } else {
        return 'Execution completed successfully with a complex result (too large to display).';
      }
    }
  } catch {
    // JSON.stringify failed
  }

  return 'Execution completed successfully, but the response format is not supported for display.';
};

// =================== Common State Updates ===================

export const executionReducers = {
  /**
   * Set current execution ID
   */
  setCurrentExecutionId: (state: any, action: PayloadAction<string | null>) => {
    state.currentExecutionId = action.payload;
  },

  /**
   * Update execution from status response
   */
  updateExecutionStatus: (state: any, action: PayloadAction<AsyncExecution>) => {
    const execution = action.payload;
    state.asyncExecutions[execution.execution_id] = execution;
    
    // Handle completion
    if (state.currentExecutionId === execution.execution_id && 
        execution.status === 'COMPLETED') {
      
      const responseText = extractResponseFromFinalOutput(execution.final_output);
      
      // Update last message for chat interface
      if (state.messages) {
        const lastMessageIndex = state.messages.length - 1;
        if (lastMessageIndex >= 0 && state.messages[lastMessageIndex].role === 'assistant') {
          state.messages[lastMessageIndex].content = [{ 
            type: 'text', 
            text: responseText 
          }];
          state.messages[lastMessageIndex].isLoading = false;
        }
      }
      
      state.currentExecutionId = null;
      state.isLoading = false;
    }
    
    // Handle failure
    if (state.currentExecutionId === execution.execution_id && 
        execution.status === 'FAILED') {
      
      // Update last message for chat interface
      if (state.messages) {
        const lastMessageIndex = state.messages.length - 1;
        if (lastMessageIndex >= 0 && state.messages[lastMessageIndex].role === 'assistant') {
          state.messages[lastMessageIndex].content = [{ 
            type: 'text', 
            text: `Error: ${execution.error_message || 'Execution failed'}` 
          }];
          state.messages[lastMessageIndex].error = true;
          state.messages[lastMessageIndex].isLoading = false;
        }
      }
      
      state.currentExecutionId = null;
      state.isLoading = false;
    }
  },

  /**
   * Update execution from stream event - FIXED with improved completion tracking
   */
  updateExecutionFromStream: (state: any, action: PayloadAction<{
    executionId: string;
    event: ExecutionProgressEvent;
  }>) => {
    const { executionId, event } = action.payload;
    const execution = state.asyncExecutions[executionId];
    
    if (!execution) return;
    
    // Update execution state
    if (event.type === 'status_update') {
      Object.assign(execution, {
        status: event.status,
        progress: event.progress || execution.progress,
        current_node: event.current_node,
        completed_nodes: event.completed_nodes || execution.completed_nodes,
        total_nodes: event.total_nodes || execution.total_nodes,
        error_message: event.error_message,
        final_output: event.final_output || execution.final_output
      });
    }
    
    // FIXED: Handle completion with better streaming state management
    if ((event.type === 'execution_finished' || (event.type === 'status_update' && event.status === 'COMPLETED')) && event.final_output) {
      execution.status = 'COMPLETED';
      execution.final_output = event.final_output;
      execution.completed_at = new Date().toISOString();
      
      //console.log('[ExecutionHelpers] ✅ Execution completed, extracting response from:', event.final_output);
      
      const responseText = extractResponseFromFinalOutput(event.final_output);
      
      // Update last message for chat interface with stronger guarantees
      if (state.messages && state.messages.length > 0) {
        const lastMessageIndex = state.messages.length - 1;
        const lastMessage = state.messages[lastMessageIndex];
        
        if (lastMessage && lastMessage.role === 'assistant') {
          // FIXED: Only update if the message is still in loading state to prevent overwriting user interactions
          if (lastMessage.isLoading !== false) {
            lastMessage.content = [{ type: 'text', text: responseText }];
            lastMessage.isLoading = false;
            lastMessage.error = false;
            
            // FIXED: Add a flag to indicate this message was updated via streaming
            lastMessage.streamingCompleted = true;
            
            /*
            console.log('[ExecutionHelpers] ✅ Updated assistant message via streaming:', {
              messageIndex: lastMessageIndex,
              responseLength: responseText.length,
              executionId
            });
            */
          } else {
            //console.log('[ExecutionHelpers] ⚠️  Skipped message update - message already finalized');
          }
        } else {
          /*
          console.warn('[ExecutionHelpers] ⚠️  No assistant message found to update:', {
            messagesLength: state.messages.length,
            lastMessageRole: lastMessage?.role,
            executionId
          });
          */
          
          // FIXED: If no assistant message exists, create one
          const newMessage = {
            role: 'assistant',
            content: [{ type: 'text', text: responseText }],
            isLoading: false,
            error: false,
            streamingCompleted: true
          };
          state.messages.push(newMessage);
          //console.log('[ExecutionHelpers] ✅ Created new assistant message via streaming');
        }
      } else {
        console.warn('[ExecutionHelpers] ⚠️  No messages array found in state');
      }
      
      if (state.currentExecutionId === executionId) {
        state.currentExecutionId = null;
        state.isLoading = false;
      }
    }
    
    // Handle failure
    if (event.type === 'status_update' && event.status === 'FAILED') {
      execution.status = 'FAILED';
      execution.error_message = event.error_message;
      execution.completed_at = new Date().toISOString();
      
      // Update last message for chat interface
      if (state.messages && state.messages.length > 0) {
        const lastMessageIndex = state.messages.length - 1;
        const lastMessage = state.messages[lastMessageIndex];
        
        if (lastMessage && lastMessage.role === 'assistant') {
          lastMessage.content = [{ type: 'text', text: `Error: ${event.error_message || 'Execution failed'}` }];
          lastMessage.error = true;
          lastMessage.isLoading = false;
          lastMessage.streamingCompleted = true;
        }
      }
      
      if (state.currentExecutionId === executionId) {
        state.currentExecutionId = null;
        state.isLoading = false;
      }
    }
  },

  /**
   * Set execution error
   */
  setExecutionError: (state: any, action: PayloadAction<{
    executionId: string;
    error: string;
  }>) => {
    const { executionId, error } = action.payload;
    const execution = state.asyncExecutions[executionId];
    
    if (execution) {
      execution.status = 'FAILED';
      execution.error_message = error;
      execution.completed_at = new Date().toISOString();
      
      // Update last message for chat interface
      if (state.messages && state.messages.length > 0) {
        const lastMessageIndex = state.messages.length - 1;
        const lastMessage = state.messages[lastMessageIndex];
        
        if (lastMessage && lastMessage.role === 'assistant') {
          lastMessage.content = [{ type: 'text', text: `Error: ${error}` }];
          lastMessage.error = true;
          lastMessage.isLoading = false;
          lastMessage.streamingCompleted = true;
        }
      }
    }
    
    if (state.currentExecutionId === executionId) {
      state.currentExecutionId = null;
      state.isLoading = false;
    }
  },

  /**
   * Clear async executions
   */
  clearAsyncExecutions: (state: any) => {
    state.asyncExecutions = {};
    state.currentExecutionId = null;
    state.streamConnections = {};
  },

  /**
   * Cleanup completed executions
   */
  cleanupCompletedExecutions: (state: any) => {
    const completedStatuses: ExecutionStatus[] = ['COMPLETED', 'FAILED', 'CANCELLED'];
    const cutoffTime = Date.now() - (5 * 60 * 1000); // 5 minutes ago
    
    for (const [id, execution] of Object.entries(state.asyncExecutions)) {
      const exec = execution as AsyncExecution;
      if (completedStatuses.includes(exec.status) && 
          exec.completed_at && 
          new Date(exec.completed_at).getTime() < cutoffTime) {
        delete state.asyncExecutions[id];
        delete state.streamConnections[id];
      }
    }
  },
};

// =================== Connection Management ===================

export const connectionReducers = {
  /**
   * Update stream connection
   */
  updateStreamConnection: (state: any, action: PayloadAction<StreamConnection>) => {
    const connection = action.payload;
    
    // Ensure serializable
    const safeConnection: StreamConnection = {
      executionId: connection.executionId,
      status: connection.status,
      attempts: connection.attempts,
      lastError: connection.lastError,
      lastConnectedAt: connection.lastConnectedAt,
      nextRetryAt: connection.nextRetryAt
    };
    
    state.streamConnections[connection.executionId] = safeConnection;
    
    // Update connection health
    const connections = Object.values(state.streamConnections) as StreamConnection[];
    const activeConnections = connections.filter(c => c.status === 'connected').length;
    const failedConnections = connections.filter(c => c.status === 'failed').length;
    
    let status: 'healthy' | 'degraded' | 'offline' = 'healthy';
    if (connections.length === 0) {
      status = 'healthy';
    } else if (failedConnections === connections.length) {
      status = 'offline';
    } else if (failedConnections > 0) {
      status = 'degraded';
    }
    
    state.connectionHealth = {
      status,
      activeConnections,
      failedConnections,
      averageReconnectionTime: state.connectionHealth?.averageReconnectionTime || 0
    };
  },

  /**
   * Remove stream connection
   */
  removeStreamConnection: (state: any, action: PayloadAction<string>) => {
    const executionId = action.payload;
    delete state.streamConnections[executionId];
  },

  /**
   * Start resilient stream
   */
  startResilientStream: (state: any, action: PayloadAction<string>) => {
    const executionId = action.payload;
    
    const connection: StreamConnection = {
      executionId,
      status: 'connecting',
      attempts: 0,
      lastError: undefined,
      lastConnectedAt: undefined,
      nextRetryAt: undefined
    };
    
    state.streamConnections[executionId] = connection;
  },
};

// =================== Message Helpers (for chat interface) ===================

export const messageHelpers = {
  /**
   * Normalize message content to always be ChatMessageContentItem[]
   */
  normalizeContent: (content: any): any[] => {
    if (typeof content === 'string') {
      try {
        const parsedContent = JSON.parse(content);
        return messageHelpers.normalizeContent(parsedContent);
      } catch (error) {
        return [{ type: 'text', text: content }];
      }
    }
    
    if (Array.isArray(content)) {
      if (content.length === 0 || (content[0] && typeof content[0] === 'object' && 'type' in content[0])) {
        return content;
      }
    }
    
    if (content && typeof content === 'object') {
      try {
        return [{ type: 'text', text: JSON.stringify(content) }];
      } catch {
        return [{ type: 'text', text: '[Unsupported object content]' }];
      }
    }
    
    return [{ type: 'text', text: '[Unsupported Content Format]' }];
  },

  /**
   * Add message to state
   */
  addMessage: (state: any, message: any) => {
    const normalizedMessage = {
      ...message,
      content: messageHelpers.normalizeContent(message.content)
    };
    state.messages.push(normalizedMessage);
  },

  /**
   * Add assistant message placeholder
   */
  addAssistantPlaceholder: (state: any) => {
    const lastMessage = state.messages[state.messages.length - 1];
    
    // FIXED: Only add placeholder if there isn't already an assistant message being loaded
    if (!lastMessage || lastMessage.role !== 'assistant' || !lastMessage.isLoading) {
      const placeholder = { 
        role: 'assistant', 
        content: [{ type: 'text', text: '' }], 
        isLoading: true,
        streamingCompleted: false
      };
      state.messages.push(placeholder);
      //console.log('[MessageHelpers] ➕ Added assistant placeholder message');
    } else {
      //console.log('[MessageHelpers] ⚡ Reusing existing assistant message placeholder');
    }
  },

  /**
   * Update last message content
   */
  updateLastMessageContent: (state: any, chunk: string, replace: boolean = false) => {
    if (state.messages.length === 0) return;
    
    const lastMessageIndex = state.messages.length - 1;
    const lastMessage = state.messages[lastMessageIndex];
    
    if (lastMessage.role === 'assistant') {
      // FIXED: Only update if streaming hasn't been completed to prevent race conditions
      if (lastMessage.streamingCompleted !== true) {
        lastMessage.isLoading = false;
        
        if (replace) {
          state.messages[lastMessageIndex].content = messageHelpers.normalizeContent(chunk);
        } else {
          const currentContentArray = lastMessage.content;
          const textItem = currentContentArray.find((c: any) => c.type === 'text');
          
          if (textItem) {
            textItem.text += chunk;
          } else {
            currentContentArray.push({ type: 'text', text: chunk });
          }
        }
        
        //console.log('[MessageHelpers] 📝 Updated message content via streaming');
      } else {
        //console.log('[MessageHelpers] ⚠️  Skipped content update - streaming already completed');
      }
    }
  },

  /**
   * Mark last message as streaming completed
   */
  markLastMessageStreamingCompleted: (state: any) => {
    if (state.messages.length === 0) return;
    
    const lastMessage = state.messages[state.messages.length - 1];
    if (lastMessage.role === 'assistant') {
      lastMessage.streamingCompleted = true;
      lastMessage.isLoading = false;
      //console.log('[MessageHelpers] ✅ Marked last assistant message as streaming completed');
    }
  },

  /**
   * Check if last message is from streaming and completed
   */
  isLastMessageStreamCompleted: (state: any): boolean => {
    if (state.messages.length === 0) return false;
    
    const lastMessage = state.messages[state.messages.length - 1];
    return lastMessage.role === 'assistant' && lastMessage.streamingCompleted === true;
  }
};

// =================== Extra Reducer Builders ===================

export function getExecutionExtraReducerHandlers() {
  // Common patterns for async thunks
  return {
    // Pattern for start execution thunks
    handleStartExecutionPending: (state: any) => {
      state.isLoading = true;
      state.error = null;
    },
    
    handleStartExecutionFulfilled: (state: any, action: any) => {
      const { response, executionData } = action.payload;
      state.isLoading = true; // Keep loading for async
      state.currentExecutionId = response.execution_id;
      state.asyncExecutions[response.execution_id] = executionData;
    },
    
    handleStartExecutionRejected: (state: any, action: any) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to start execution';
      
      if (state.messages) {
        const errorMessage = {
          role: 'assistant',
          content: [{ type: 'text', text: `Error: ${state.error}` }],
          error: true,
          isLoading: false,
          streamingCompleted: true
        };
        state.messages.push(errorMessage);
      }
    },
    
    // Pattern for cancel execution thunks
    handleCancelExecutionFulfilled: (state: any, action: any) => {
      const executionId = action.payload;
      
      if (state.asyncExecutions[executionId]) {
        state.asyncExecutions[executionId].status = 'CANCELLED';
        state.asyncExecutions[executionId].completed_at = new Date().toISOString();
      }
      
      if (state.currentExecutionId === executionId) {
        if (state.messages && state.messages.length > 0) {
          const lastMessageIndex = state.messages.length - 1;
          const lastMessage = state.messages[lastMessageIndex];
          
          if (lastMessage && lastMessage.role === 'assistant') {
            lastMessage.content = [{ 
              type: 'text', 
              text: 'Execution was cancelled.' 
            }];
            lastMessage.isLoading = false;
            lastMessage.streamingCompleted = true;
          }
        }
        
        state.currentExecutionId = null;
        state.isLoading = false;
      }
      
      // Remove connection
      delete state.streamConnections[executionId];
    },
  };
}